// What the retainer ABN gate shows (RETAINER_ABN_SUPPLIER_PLAN.md 5.8). Pure, so the
// interlock rules are unit-tested (retainerGate.test.mjs).
//
// Modes:
//   'none'          render nothing
//   'loading'       neutral full-screen "Loading..." (blocks the portal, starts nothing)
//   'config_error'  full-screen "We couldn't load your details" + Retry (blocks)
//   'flow_error'    same, for the private flow doc
//   'starting'      /pays: welcome screen with a spinner while startRetainerFlow runs
//   'start_failed'  /pays: welcome screen with Retry
//   'flow'          the locked flow (step from the flow doc)
//   'success'       "You're all set" until the host presses Done (this session only)

export const BLOCKING_MODES = new Set(['loading', 'config_error', 'flow_error', 'starting', 'start_failed', 'flow', 'success']);

export function gateMode(s) {
  // Sign-in still resolving: on a brand known (even from cache) to have used the flow, block
  // with the neutral loading screen so the header and menu are never usable before we know.
  if (s.authLoading) return s.everEnabled === true ? 'loading' : 'none';
  if (!s.hasUser || s.impersonating) return 'none';

  // 1) Brand config: a missing/false marker only counts once the server confirmed it.
  if (s.everEnabled !== true) {
    if (s.configStatus === 'server') return 'none';
    if (s.configStatus === 'error') return 'config_error';
    return 'loading';
  }

  // 2) The brand has used the flow: this host may have one.
  if (!s.hasProfile) {
    if (s.configStatus === 'server') return 'none';
    return s.configStatus === 'error' ? 'config_error' : 'loading';
  }

  const flow = s.flow || { state: 'unknown' };
  if (flow.state === 'present') {
    if (flow.step === 'done') return s.finishedThisSession ? 'success' : 'none';
    return 'flow';
  }
  if (flow.state === 'unknown') return 'loading';
  if (flow.state === 'error') return 'flow_error';

  // 3) Server-confirmed: no flow yet. Only /pays may start one, on a server-confirmed config.
  if (s.finishedThisSession) return 'success';
  if (!s.isPays) return 'none';
  if (s.configStatus !== 'server') return s.configStatus === 'error' ? 'config_error' : 'loading';
  if (s.enabled !== true || s.flagFalse !== true || s.startState === 'not_enrolled') return 'none';
  return s.startState === 'failed' ? 'start_failed' : 'starting';
}

// The legacy /pays popup + embed + flag write may only run on a brand the server
// confirmed has never used the ABN flow.
export function legacyRetainerAllowed({ configStatus, everEnabled }) {
  return configStatus === 'server' && everEnabled !== true;
}

export function isPaysPath(pathname) {
  return String(pathname || '').replace(/\/+$/, '') === '/pays';
}

// Owner D2 + D8 (mirrors the server): remove spaces, exactly 11 digits, and the ATO check digit
// (subtract 1 from the first digit; the weighted sum must divide by 89). Offline maths, no lookup.
const ABN_WEIGHTS = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
export function abnLooksRight(raw) {
  const s = String(raw || '').replace(/\s+/g, '');
  if (!/^[0-9]{11}$/.test(s)) return false;
  let sum = 0;
  for (let i = 0; i < 11; i += 1) sum += (Number(s[i]) - (i === 0 ? 1 : 0)) * ABN_WEIGHTS[i];
  return sum % 89 === 0;
}

// Manager-facing wording for one listRetainerPaperwork row (plan 5.8). tone: MUI colour or 'neutral'.
const STEP_TEXT = {
  rcti: 'Retainer form not sent yet',
  abn_missing: 'Waiting for their ABN or supplier form',
  supplier: 'Filling out the supplier form'
};
const DONE_TEXT = {
  provided: 'Done: ABN given',
  entered_in_app: 'Done: ABN entered in the app',
  supplier_statement: 'Done: supplier form',
  manager_override: 'Done: marked done by a manager'
};
export function paperworkLabel(row) {
  const r = row || {};
  if (r.status === 'not_started') {
    if (r.needsPaperwork === true) {
      return {
        text: "Not started yet: hasn't opened Host Payments",
        tone: 'warning',
        detail: "They'll be asked for their paperwork the next time they open Host Payments."
      };
    }
    return { text: 'Not started', tone: 'neutral', detail: '' };
  }
  if (r.status === 'in_progress') {
    if (r.problem === 'too_many_submissions') {
      return { text: 'Stuck: too many submissions', tone: 'error', detail: 'Contact the host, then use Mark paperwork done.' };
    }
    if (r.problem === 'abn_question_missing') {
      return { text: "Stuck: the form's ABN question wasn't found", tone: 'error', detail: 'Check the ABN field name in Edit brand.' };
    }
    return { text: `In progress: ${STEP_TEXT[r.step] || r.step || 'started'}`, tone: 'warning', detail: '' };
  }
  if (r.jobPhase === 'needs_manager') {
    return {
      text: 'Needs attention: add the ABN to Jotform',
      tone: 'error',
      detail: `ABN ${r.abn || ''}, retainer form submission ${r.rctiSubmissionId || ''}`
    };
  }
  if (r.jobPhase === 'writeback' || r.jobPhase === 'notify') {
    return { text: `${DONE_TEXT[r.abnStatus] || 'Done'} (copying the ABN to Jotform)`, tone: 'success', detail: '' };
  }
  return { text: DONE_TEXT[r.abnStatus] || 'Done', tone: 'success', detail: '' };
}

// Start a flow once for the session `key` (Code review R1 M1). Marks the session 'starting',
// calls start(), then records 'started' / 'not_enrolled' / 'failed', but only if the session
// still has the same key: nothing else can cancel the answer, and an answer for a previous
// identity (user, brand or users doc) is dropped. setSession is a React state setter.
const freshSession = (key) => ({ key, startState: 'idle', seenUnfinished: false, finished: false });
export function startRetainerFlowOnce({ key, start, setSession }) {
  const settle = (startState) => setSession((cur) => (cur && cur.key === key ? { ...cur, startState } : cur));
  setSession((cur) => ({ ...(cur && cur.key === key ? cur : freshSession(key)), startState: 'starting' }));
  let p;
  try {
    p = Promise.resolve(start());
  } catch {
    p = Promise.reject(new Error('start threw'));
  }
  return p.then(
    (r) => settle(r && r.enrolled === false ? 'not_enrolled' : 'started'),
    () => settle('failed')
  );
}
